import { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { authService } from '../../services/auth.service';
import { catalogService, CatalogItem } from '../../services/catalog.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

type SectionKey =
  | 'general'
  | 'security'
  | 'limits'
  | 'governance'
  | 'maintenance'
  | 'monitor';

const SECTIONS: Array<{ key: SectionKey; label: string; icon: string }> = [
  { key: 'general', label: 'General', icon: '⚙️' },
  { key: 'security', label: 'Seguridad', icon: '🔒' },
  { key: 'limits', label: 'Límites APIs', icon: '📊' },
  { key: 'governance', label: 'Gobernanza', icon: '📋' },
  { key: 'monitor', label: 'Cloudflare', icon: '☁️' },
  { key: 'maintenance', label: 'Mantenimiento', icon: '🛠️' },
];

const ROLE_LABELS: Record<string, string> = {
  sys_admin: 'Administrador',
  general_manager: 'Gerente General',
  project_lead: 'Líder Proyecto',
  accountant: 'Contador',
  contractor: 'Contratista',
  receptionist: 'Recepcionista',
  data_entry: 'Capturista',
  client: 'Cliente',
};

const RBAC_PERMISSIONS: Array<{ key: string; label: string }> = [
  { key: 'view_all', label: 'Ver todo' },
  { key: 'financial', label: 'Financiero' },
  { key: 'project_approval', label: 'Aprobar proyectos' },
  { key: 'project_manage', label: 'Gestionar proyectos' },
  { key: 'team_assign', label: 'Asignar equipos' },
  { key: 'invoicing', label: 'Facturación' },
  { key: 'reports', label: 'Reportes' },
  { key: 'progress_report', label: 'Reportar avance' },
  { key: 'client_register', label: 'Registrar clientes' },
  { key: 'data_input', label: 'Capturar datos' },
  { key: 'user_management', label: 'Gestión usuarios' },
  { key: 'system_config', label: 'Config sistema' },
];

const DEFAULT_RBAC: Record<string, string[]> = {
  sys_admin: RBAC_PERMISSIONS.map((p) => p.key),
  general_manager: ['view_all', 'financial', 'project_approval', 'reports', 'project_manage'],
  project_lead: ['project_manage', 'team_assign', 'progress_report', 'project_approval'],
  accountant: ['financial', 'invoicing', 'reports'],
  contractor: ['progress_report'],
  receptionist: ['client_register'],
  data_entry: ['data_input'],
  client: [],
};

export function SystemSettingsPage() {
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [activeSection, setActiveSection] = useState<SectionKey>('general');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [general, setGeneral] = useState({
    appName: 'GranArqui',
    version: '1.03',
    timezone: 'America/Monterrey',
    currency: 'MXN',
  });

  const [security, setSecurity] = useState({
    mfaRequired: true,
    sessionTimeout: 60,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
  });

  const [limits, setLimits] = useState({
    clerkUsersLimit: 10000,
    resendEmailsLimit: 3000,
    workersAiLimit: 10000,
    openaiMonthlyLimit: 100,
  });

  const [governance, setGovernance] = useState({
    dataRetentionDays: 90,
    backupFrequency: 'daily',
    backupRetention: 7,
    allowExport: true,
    syncConflictResolution: 'timestamp',
  });

  const [rbac, setRbac] = useState<Record<string, string[]>>(DEFAULT_RBAC);
  const [editingRbac, setEditingRbac] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('sys_admin');

  const [monitor, setMonitor] = useState<{
    workersStatus: string;
    d1Status: string;
    kvStatus: string;
    r2Status: string;
    lastCheck: number;
  } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [configRes, monitorRes] = await Promise.all([
        authService.fetchWithAuth(`${API_URL}/api/system/config`).catch(() => null),
        authService.fetchWithAuth(`${API_URL}/api/system/monitor`).catch(() => null),
      ]);

      const config = configRes?.data;
      if (Array.isArray(config)) {
        const map: Record<string, string> = {};
        config.forEach((item: any) => {
          map[item.key] = item.value;
        });
        if (map['app.name']) setGeneral((g) => ({ ...g, appName: map['app.name'].replace(/"/g, '') }));
        if (map['app.version']) setGeneral((g) => ({ ...g, version: map['app.version'].replace(/"/g, '') }));
        if (map['app.timezone']) setGeneral((g) => ({ ...g, timezone: map['app.timezone'].replace(/"/g, '') }));
        if (map['security.session_timeout']) {
          setSecurity((s) => ({ ...s, sessionTimeout: parseInt(map['security.session_timeout']) || 60 }));
        }
      }

      if (monitorRes?.data) {
        setMonitor(monitorRes.data);
      } else {
        setMonitor({
          workersStatus: 'unknown',
          d1Status: 'unknown',
          kvStatus: 'unknown',
          r2Status: 'unknown',
          lastCheck: Date.now(),
        });
      }
    } catch (e: any) {
      setError(e?.message || 'Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSaveGeneral = async () => {
    setIsSaving(true);
    try {
      await authService.fetchWithAuth(`${API_URL}/api/system/config`, {
        method: 'PUT',
        body: JSON.stringify({
          'app.name': JSON.stringify(general.appName),
          'app.timezone': JSON.stringify(general.timezone),
        }),
      });
      showSuccess('Configuración general guardada');
    } catch (e: any) {
      showError(e?.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSecurity = async () => {
    setIsSaving(true);
    try {
      await authService.fetchWithAuth(`${API_URL}/api/system/config`, {
        method: 'PUT',
        body: JSON.stringify({
          'security.session_timeout': String(security.sessionTimeout),
          'security.max_login_attempts': String(security.maxLoginAttempts),
          'security.2fa_enabled': String(security.mfaRequired),
        }),
      });
      showSuccess('Seguridad guardada');
    } catch (e: any) {
      showError(e?.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveLimits = async () => {
    setIsSaving(true);
    try {
      await authService.fetchWithAuth(`${API_URL}/api/system/config`, {
        method: 'PUT',
        body: JSON.stringify({
          'limits.clerk_users': String(limits.clerkUsersLimit),
          'limits.resend_emails': String(limits.resendEmailsLimit),
          'limits.workers_ai': String(limits.workersAiLimit),
          'limits.openai_monthly': String(limits.openaiMonthlyLimit),
        }),
      });
      showSuccess('Límites guardados');
    } catch (e: any) {
      showError(e?.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveGovernance = async () => {
    setIsSaving(true);
    try {
      await authService.fetchWithAuth(`${API_URL}/api/system/config`, {
        method: 'PUT',
        body: JSON.stringify({
          'governance.retention_days': String(governance.dataRetentionDays),
          'governance.backup_frequency': governance.backupFrequency,
          'governance.backup_retention': String(governance.backupRetention),
          'governance.allow_export': String(governance.allowExport),
          'governance.conflict_resolution': governance.syncConflictResolution,
        }),
      });
      showSuccess('Gobernanza guardada');
    } catch (e: any) {
      showError(e?.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleRbacPermission = (role: string, permission: string) => {
    setRbac((prev) => {
      const current = prev[role] || [];
      const next = current.includes(permission)
        ? current.filter((p) => p !== permission)
        : [...current, permission];
      return { ...prev, [role]: next };
    });
  };

  const handleSaveRbac = async () => {
    setIsSaving(true);
    try {
      await authService.fetchWithAuth(`${API_URL}/api/system/rbac`, {
        method: 'PUT',
        body: JSON.stringify({ roles: rbac }),
      });
      showSuccess('Matriz RBAC actualizada');
      setEditingRbac(false);
    } catch (e: any) {
      showError(e?.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmergencyAction = async (action: string, label: string) => {
    if (
      !(await confirm({
        message: `¿Ejecutar "${label}"? Esta acción no se puede deshacer.`,
        danger: true,
        confirmLabel: 'Ejecutar',
      }))
    ) {
      return;
    }
    try {
      await authService.fetchWithAuth(`${API_URL}/api/system/emergency`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      showSuccess(`${label} ejecutado`);
    } catch (e: any) {
      showError(e?.message || 'Error al ejecutar');
    }
  };

  if (loading) return <LoadingState message="Cargando configuración..." />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Configuración del Sistema</h1>
        <p className="text-xs text-slate-500">Ajustes globales y gobernanza</p>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
              activeSection === s.key
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {activeSection === 'general' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-bold mb-2">Configuración General</h2>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Nombre de la App</label>
            <input
              type="text"
              value={general.appName}
              onChange={(e) => setGeneral({ ...general, appName: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Versión</label>
            <input
              type="text"
              value={general.version}
              disabled
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 text-slate-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Zona Horaria</label>
            <select
              value={general.timezone}
              onChange={(e) => setGeneral({ ...general, timezone: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            >
              <option value="America/Monterrey">Monterrey</option>
              <option value="America/Mexico_City">Ciudad de México</option>
              <option value="America/Tijuana">Tijuana</option>
              <option value="America/Cancun">Cancún</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Moneda</label>
            <select
              value={general.currency}
              onChange={(e) => setGeneral({ ...general, currency: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            >
              <option value="MXN">MXN - Peso Mexicano</option>
              <option value="USD">USD - Dólar</option>
            </select>
          </div>
          <button
            onClick={handleSaveGeneral}
            disabled={isSaving}
            className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
          >
            {isSaving ? 'Guardando...' : '💾 Guardar'}
          </button>
        </div>
      )}

      {activeSection === 'security' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-bold mb-2">Seguridad</h2>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={security.mfaRequired}
              onChange={(e) => setSecurity({ ...security, mfaRequired: e.target.checked })}
              className="w-4 h-4 accent-primary"
            />
            MFA obligatorio para administradores
          </label>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Tiempo de sesión (minutos)
            </label>
            <input
              type="number"
              min={5}
              max={480}
              value={security.sessionTimeout}
              onChange={(e) => setSecurity({ ...security, sessionTimeout: parseInt(e.target.value) || 60 })}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Intentos fallidos antes de bloqueo
            </label>
            <input
              type="number"
              min={3}
              max={10}
              value={security.maxLoginAttempts}
              onChange={(e) => setSecurity({ ...security, maxLoginAttempts: parseInt(e.target.value) || 5 })}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <button
            onClick={handleSaveSecurity}
            disabled={isSaving}
            className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
          >
            {isSaving ? 'Guardando...' : '💾 Guardar'}
          </button>
        </div>
      )}

      {activeSection === 'limits' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-bold mb-2">Límites de APIs</h2>
          {[
            { key: 'clerkUsersLimit', label: 'Clerk - Usuarios activos', max: 10000 },
            { key: 'resendEmailsLimit', label: 'Resend - Emails/mes', max: 3000 },
            { key: 'workersAiLimit', label: 'Workers AI - Requests/día', max: 10000 },
            { key: 'openaiMonthlyLimit', label: 'OpenAI - Costo mensual ($)', max: 100 },
          ].map((item) => (
            <div key={item.key}>
              <label className="block text-xs font-semibold text-slate-700 mb-1">{item.label}</label>
              <input
                type="number"
                min={0}
                value={(limits as any)[item.key]}
                onChange={(e) =>
                  setLimits({ ...limits, [item.key]: parseInt(e.target.value) || 0 })
                }
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          ))}
          <button
            onClick={handleSaveLimits}
            disabled={isSaving}
            className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
          >
            {isSaving ? 'Guardando...' : '💾 Guardar'}
          </button>
        </div>
      )}

      {activeSection === 'governance' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-bold mb-2">Gobernanza de Datos</h2>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Retención de datos (días)
            </label>
            <input
              type="number"
              min={30}
              value={governance.dataRetentionDays}
              onChange={(e) => setGovernance({ ...governance, dataRetentionDays: parseInt(e.target.value) || 90 })}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Frecuencia de backup</label>
            <select
              value={governance.backupFrequency}
              onChange={(e) => setGovernance({ ...governance, backupFrequency: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            >
              <option value="daily">Diario</option>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensual</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Backups a retener
            </label>
            <input
              type="number"
              min={1}
              max={30}
              value={governance.backupRetention}
              onChange={(e) => setGovernance({ ...governance, backupRetention: parseInt(e.target.value) || 7 })}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={governance.allowExport}
              onChange={(e) => setGovernance({ ...governance, allowExport: e.target.checked })}
              className="w-4 h-4 accent-primary"
            />
            Permitir exportación de datos por usuarios
          </label>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Resolución de conflictos offline
            </label>
            <select
              value={governance.syncConflictResolution}
              onChange={(e) => setGovernance({ ...governance, syncConflictResolution: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            >
              <option value="timestamp">Por timestamp (más reciente gana)</option>
              <option value="server">Servidor gana siempre</option>
              <option value="client">Cliente gana siempre</option>
              <option value="manual">Manual (requiere confirmación)</option>
            </select>
          </div>

          <div className="pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold">Matriz de Roles (RBAC)</h3>
              <button
                onClick={() => setEditingRbac(!editingRbac)}
                className="text-xs text-primary font-semibold"
              >
                {editingRbac ? 'Cancelar' : 'Editar'}
              </button>
            </div>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm mb-2"
            >
              {Object.keys(ROLE_LABELS).map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
            <div className="space-y-1">
              {RBAC_PERMISSIONS.map((perm) => {
                const enabled = (rbac[selectedRole] || []).includes(perm.key);
                return (
                  <label
                    key={perm.key}
                    className="flex items-center gap-2 text-xs py-1 px-2 rounded hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={enabled}
                      disabled={!editingRbac}
                      onChange={() => handleToggleRbacPermission(selectedRole, perm.key)}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className={enabled ? 'text-slate-900 font-medium' : 'text-slate-500'}>
                      {perm.label}
                    </span>
                  </label>
                );
              })}
            </div>
            {editingRbac && (
              <button
                onClick={handleSaveRbac}
                disabled={isSaving}
                className="w-full mt-2 py-2 rounded-lg bg-primary text-white font-semibold text-xs disabled:opacity-50"
              >
                {isSaving ? 'Guardando...' : '💾 Guardar Matriz'}
              </button>
            )}
          </div>

          <button
            onClick={handleSaveGovernance}
            disabled={isSaving}
            className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
          >
            {isSaving ? 'Guardando...' : '💾 Guardar'}
          </button>
        </div>
      )}

      {activeSection === 'monitor' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-bold mb-2">Monitor de Cloudflare</h2>
          {monitor && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Workers', status: monitor.workersStatus },
                  { label: 'D1 DB', status: monitor.d1Status },
                  { label: 'KV Cache', status: monitor.kvStatus },
                  { label: 'R2 Storage', status: monitor.r2Status },
                ].map((s) => (
                  <div key={s.label} className="bg-slate-50 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500">{s.label}</p>
                    <p className={`text-xs font-bold ${
                      s.status === 'up' || s.status === 'healthy'
                        ? 'text-emerald-600'
                        : s.status === 'degraded'
                        ? 'text-amber-600'
                        : 'text-red-600'
                    }`}>
                      {s.status === 'up' || s.status === 'healthy' ? '🟢 Operativo' :
                       s.status === 'degraded' ? '🟡 Degradado' :
                       s.status === 'unknown' ? '⚪ Desconocido' : '🔴 Caído'}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 text-center">
                Última verificación: {new Date(monitor.lastCheck).toLocaleString('es-MX')}
              </p>
            </>
          )}
          <button
            onClick={load}
            className="w-full py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
          >
            🔄 Re-verificar
          </button>
        </div>
      )}

      {activeSection === 'maintenance' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-bold mb-2">Acciones de Emergencia</h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2">
            <p className="text-xs text-red-700 font-medium">⚠️ Estas acciones afectan a todos los usuarios</p>
          </div>
          <button
            onClick={() => handleEmergencyAction('purge_cache', 'Purgar caché KV')}
            className="w-full py-2.5 rounded-lg bg-amber-50 text-amber-700 font-semibold text-sm"
          >
            🗑️ Purgar caché KV
          </button>
          <button
            onClick={() => handleEmergencyAction('enable_maintenance', 'Modo mantenimiento')}
            className="w-full py-2.5 rounded-lg bg-amber-50 text-amber-700 font-semibold text-sm"
          >
            🚧 Modo Mantenimiento
          </button>
          <button
            onClick={() => handleEmergencyAction('force_sync', 'Forzar sincronización')}
            className="w-full py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
          >
            🔄 Forzar sincronización
          </button>
          <button
            onClick={() => handleEmergencyAction('clear_offline_data', 'Limpiar datos offline')}
            className="w-full py-2.5 rounded-lg bg-red-50 text-red-700 font-semibold text-sm"
          >
            🗑️ Limpiar datos offline
          </button>
        </div>
      )}
    </div>
  );
}